import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import get from "lodash/get";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import displayError from '../../utilities/displayError';

const customStyles = {
  control: (provided, { isFocused }) => {
    const focusColors = isFocused ? { boxShadow: 0 } : {};

    return {
      ...provided,
      ...focusColors,
      height: 42,
      borderColor: isFocused ? '#121212' : '#DDE0E6',
      "&:hover": {
        borderColor: isFocused ? '#121212' : '#DDE0E6',
      }
    };
  },

  placeholder: (provided) => {
    return {
      ...provided,
      color: '#CED2DA'
    };
  },

  option: (provided, { isSelected, isFocused }) => ({
    ...provided,
    backgroundColor: isSelected
      ? '#F8F9FA'
      : isFocused
        ? '#F8F9FA'
        : null,
    color: '#343840',
    padding: 8
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#343840'
  }),

  indicatorSeparator: () => ({
    display: 'none'
  }),

  menu: (provided) => ({
    ...provided,
    marginTop: 0,
    boxShadow: 'none',
    border: '1px solid #DDE0E6'
  })
};

const customTheme = (theme) => ({
  ...theme,
  borderRadius: 2,
  colors: {
    ...theme.colors,
    primary: '#DDE0E6',
    primary75: '#E9EBEF',
    primary50: '#E9EBEF',
    primary25: '#E9EBEF'
  }
});


const SelectField = ({
  className = '',
  inputClassName = '',
  selectClassNames = {},
  options = [],
  input = {},
  meta = {},
  styles = null,
  placeholder = null,
  errorMessage = null,
  maxMenuHeight = 300,
  id = '',
  attributes = {},
  allOptions = [],
  isDisabled,
  classNamePrefix,
  menuPlacement = 'bottom',
  isMobile,
  isSearchable = false,
  formatOptionLabel = null,
  defaultValue = null,
  menuShouldScrollIntoView = true
}) => {
  const { error, submitFailed } = meta;
  const selectFieldClassNames = classNames('SelectField', className);
  const { name, value, onChange } = input;
  // This is so that react-select can tie into redux form's methods
  const handleOnChange = (changeValue) => {
    const val = get(changeValue, 'value');
    if (val || 0 === val) {
      onChange(val);
    }
  };

  const nativeHandleOnChange = (e) => {
    const val = get(e, 'target.value');
    if (val || 0 === val) {
      onChange(val);
    }
  };


  const reduxFormValue = !isEmpty(allOptions) ?
    find(allOptions, { value })
    :
    find(options, { value });

  const nativeSelectClass = classNames('btn btn-default btn-outlined-light dropdown-btn remove-click-styling', { 'text-gray-400': !reduxFormValue})
  return (
    <div className={selectFieldClassNames} {...attributes}>
      {
        isMobile ?
          <div className="NativeSelect">
            <select id={id} onChange={nativeHandleOnChange} className={nativeSelectClass} defaultValue="">
              <option disabled value="">Select Option</option>
              {
                map(options || [], (option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))
              }
            </select>
            <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="arrowDown">
              <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
            </svg>
          </div>
          :
          <Select
            name={name}
            value={reduxFormValue || null}
            className={inputClassName}
            classNames={selectClassNames}
            placeholder={placeholder}
            options={options}
            onChange={handleOnChange}
            styles={styles || customStyles}
            theme={customTheme}
            isSearchable={isSearchable}
            maxMenuHeight={maxMenuHeight}
            inputId={id}
            isDisabled={isDisabled}
            classNamePrefix={classNamePrefix}
            menuPlacement={menuPlacement}
            formatOptionLabel={formatOptionLabel}
            defaultValue={defaultValue}
            menuShouldScrollIntoView={menuShouldScrollIntoView}
          />
      }
      { !!(error && submitFailed) &&
        <p className="error text-danger text-small">
          {displayError(errorMessage, error)}
        </p>
      }
    </div>
  );
};

SelectField.propTypes = {
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  options: PropTypes.array,
  input: PropTypes.object,
  meta: PropTypes.object,
  placeholder: PropTypes.string,
  errorMessage: PropTypes.string,
  styles: PropTypes.object,
  id: PropTypes.string,
  removeSelections: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  attributes: PropTypes.object,
  selectClassNames: PropTypes.object,
  allOptions: PropTypes.array,
  isDisabled: PropTypes.bool,
  classNamePrefix: PropTypes.string,
  menuPlacement: PropTypes.string,
  isSearchable: PropTypes.boolean
};

export default SelectField;
